import { GetStaticPaths, GetStaticProps } from "next";
import Image from "next/image";
import Link from "next/link";
import { ParsedUrlQuery } from "querystring";
import { useMedia } from "react-use";
import SubPageLayout from "../../../components/common/layouts/SubPageLayout";
import {
  Pagination,
  buildPageNumArray,
} from "../../../components/common/ui/Pagination";
import { MOBILE_MAX_WIDTH } from "../../../constant";
import { dateFormat } from "../../../services/dateFormat";
import {
  buildNewsListPath,
  buildNewsSrc,
  fetchNewsList,
} from "../../../services/news";
import styles from "../../../styles/News.module.scss";
import { News, NewsArray } from "../../../types/newsType";
interface Paths extends ParsedUrlQuery {
  num: string;
}

type Props = {
  pageNum: number;
  contents: NewsArray;
  totalCount: number;
};

const PER_PAGE = 9;

export const getStaticPaths: GetStaticPaths<Paths> = async () => {
  const data = await fetchNewsList({ limit: 0 });

  const totalCount = data?.totalCount ?? 0;
  const paths = buildPageNumArray({ totalCount, perPage: PER_PAGE }).map(
    (n) => ({
      params: { num: String(n) },
    })
  );

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<Props, Paths> = async ({
  params,
}) => {
  const pageNum = Number(params!.num);
  const offset = (pageNum - 1) * PER_PAGE;
  const data = await fetchNewsList({
    offset,
    limit: PER_PAGE,
  });

  return {
    props: { pageNum, ...(data ?? { contents: [], totalCount: 0 }) },
  };
};

const MAX_SHOWN_LENGTH = 100;
const ELLIPSIS = "...";
const ELLIPSIS_LENGTH = ELLIPSIS.length;
const truncate = (s: string): string =>
  s.length <= MAX_SHOWN_LENGTH
    ? s
    : s.slice(0, MAX_SHOWN_LENGTH - ELLIPSIS_LENGTH) + ELLIPSIS;

const NewsWithPagination = ({ pageNum, contents, totalCount }: Props) => {
  const isMobile = useMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`, false);

  return (
    <SubPageLayout
      title="NEWS"
      titleImgSrc="/images/text_images/news_vertical.svg"
      bgImgUrl="/images/bg-gallery-pc.jpg"
      spBgImgUrl="/images/bg-gallery-sp.jpg"
      isMobile={isMobile}
      extraContainerClass={
        isMobile ? "mt-[37px]" : "min-h-[calc(100vh-134px)] mt-[54px]"
      }
    >
      <>
        <div className={styles.newsRowsArea}>
          {contents.map((news) => (
            <NewsRow key={news.id} news={news} isMobile={isMobile} />
          ))}
        </div>
        <Pagination
          totalCount={totalCount}
          perPage={PER_PAGE}
          pageNum={pageNum}
          buildPath={buildNewsListPath}
        />
      </>
    </SubPageLayout>
  );
};

const NewsRow = ({
  news,
  isMobile,
}: {
  news: Omit<News, "content">;
  isMobile: boolean;
}) => (
  <Link key={news.id} href={`/news/${news.id}`} passHref>
    <a className={styles.newsRow}>
      <div className={styles.newsRowLeft}>
        <div className={styles.dot} />
        {!isMobile && (
          <div className={styles.date}>{dateFormat(news.publishedAt)}</div>
        )}
      </div>
      <div className={styles.newsRowCard}>
        <div className={styles.imageWrapper}>
          <Image
            src={buildNewsSrc(news) || "/images/decoration/news_thumb.png"}
            alt="News Thumb"
            layout="fill"
            objectFit="cover"
            objectPosition="top"
          />
        </div>
        <div className={styles.cardTextArea}>
          {isMobile && (
            <div className={styles.date}>{dateFormat(news.publishedAt)}</div>
          )}
          <div>{truncate(news.title)}</div>
        </div>
      </div>
    </a>
  </Link>
);

export default NewsWithPagination;
